import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Layers, Mail, Lock, User, AlertCircle } from 'lucide-react';

export default function Register() {
  const { user, loading, register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e8744f] mx-auto mb-3" />
        <p className="text-sm text-gray-400 animate-pulse">Loading...</p>
      </div>
    </div>
  );

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await register(email, password, name);
    if (!result.success) setError(result.error);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd] flex items-center justify-center px-4">

      {/* Ambient blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[#e8744f]/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-[#fae5dd]/60 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">

        {/* Card */}
        <div className="rounded-3xl border border-orange-100 bg-white/80 backdrop-blur-sm shadow-xl p-8 space-y-7">

          {/* Logo + heading */}
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#e8744f] to-[#d66440] shadow-md">
              <Layers className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Join Folio</h1>
              <p className="text-sm text-gray-500 mt-0.5">Create your portfolio account</p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
              <AlertCircle size={15} className="mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Name
              </label>
              <div className="relative">
                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your Name"
                  required
                  data-testid="register-name-input"
                  className="pl-9 rounded-xl border-gray-200 bg-white/70 text-sm focus:border-[#e8744f] focus:ring-2 focus:ring-orange-100 transition"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Email
              </label>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="register-email-input"
                  className="pl-9 rounded-xl border-gray-200 bg-white/70 text-sm focus:border-[#e8744f] focus:ring-2 focus:ring-orange-100 transition"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Password
              </label>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength="6"
                  data-testid="register-password-input"
                  className="pl-9 rounded-xl border-gray-200 bg-white/70 text-sm focus:border-[#e8744f] focus:ring-2 focus:ring-orange-100 transition"
                />
              </div>
              <p className="text-xs text-gray-400 pl-1">Minimum 6 characters</p>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="register-submit-button"
              className="w-full rounded-full bg-[#e8744f] hover:bg-[#d66440] text-white py-5 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200 mt-2"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  Creating account...
                </span>
              ) : 'Sign Up'}
            </Button>
          </form>

          {/* Divider + log in */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-gray-400">or</span>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-[#e8744f] hover:underline">
              Log in
            </Link>
          </p>
        </div>

        {/* Bottom note */}
        <p className="text-center text-xs text-gray-400 mt-5">
          Portfolio matching platform · Made with ✨
        </p>
      </div>
    </div>
  );
}